import { useState } from 'react';

import {
  CasBaseDialog,
  CasButton,
  CasDisplayField,
  CasLoaderWithBackdrop,
  CasStackRow,
  CasTable,
} from 'src/compass/shared/components';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';
import getCasTableCellValue from 'src/compass/shared/components/CasTable/getCasTableCellValue';
import { useRetrieveDetailsById } from 'src/compass/shared/hooks/reactQuery/useRetrieveDetailsById';
import useDownloadFileMutation from 'src/compass/shared/hooks/reactQuery/useDownloadFileMutation';
import { CasSecondaryButton } from 'src/compass/shared/components/CasButton/CasSecondaryButton';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import { getParsedHTML } from 'src/compass/shared/util/getParsedHTML';
import ClaimNotesField from './ClaimNotesField';
import GrowerDialog from './GrowerDialog';

type Props = {
  onClose: VoidFunction;
  id: number;
  onUpdated: Function;
  showMarkClosedBtn?: boolean;
  onViewClaimDetails?: VoidFunction;
};

export default function RvwUploadDialog({
  id,
  showMarkClosedBtn = true,
  onClose,
  onUpdated,
  onViewClaimDetails,
}: Props) {
  const growerDialog = useCasBoolean();

  const [hasCopiedFiles, setHasCopiedFiles] = useState(false);

  const { data, isLoading, isError, isRefetching, refetch } = useRetrieveDetailsById({
    entity: 'Sce/Claims/RvwUpload',
    id,
  });

  const { mutate } = useUpdateMutation({
    url: 'Sce/Claims/RvwUpload/MarkClose',
    onSuccess: (response) => {
      onSuccessEnqueueSnackbar(response);
      onClose();
      onUpdated();
    },
  });

  const { mutate: downloadMutate } = useDownloadFileMutation({
    url: 'Sce/Claims/RvwUpload/Download',
    showLoader: false,
  });

  const { mutate: openUploadsFolderInNewTab } = useAddMutation({
    url: 'Sce/Claims/RvwUpload/UploadFolderUrl',
    params: { growerId: data?.growerId },
    onSuccess: (response: any) => {
      if(response.uploadFolderUrl) window.open(response.uploadFolderUrl, '_blank', 'noopener');
    },
  });

  const { mutate: copyToSharepointMutate } = useUpdateMutation({
    url: 'Sce/Claims/RvwUpload/CopyToSharepoint',
    params: { id },
    onSuccess: (response) => {
      onSuccessEnqueueSnackbar(response);
      setHasCopiedFiles(true);
      openUploadsFolderInNewTab();
    },
  });
  
  if (isLoading) return <CasLoaderWithBackdrop />;

  if (isError) throw new Error('Error retrieving data from the server.');

  return (
    <CasBaseDialog
      open
      fullScreen
      onClose={onClose}
      title="Review Upload"
      showCloseIcon
      actions={
        showMarkClosedBtn && data?.status !== 'Closed' && (
          <CasSecondaryButton onClick={() => mutate({ id })}>Mark Closed</CasSecondaryButton>
        )
      }
    >
      {isRefetching && <CasLoaderWithBackdrop/> }

      <CasStackRow className='bordered-cells'>
        <CasDisplayField label="Insured Name" value={data?.insuredName} />
        <CasDisplayField label="Year" value={data?.reinsuranceYear} />
        <CasDisplayField label="Policy" value={data?.policyNumber} />
        <CasDisplayField label="Sce" value={data?.sce} />
        <CasDisplayField
          label="Effective"
          value={formatDate(data?.effectiveDate, DateFormat.MDY)}
        />
        <CasDisplayField label="End" value={formatDate(data?.endDate, DateFormat.MDY)} />
      </CasStackRow>
      <CasStackRow className='bordered-cells'>
        <CasDisplayField
          label="Date Submitted"
          value={formatDate(data?.dateSubmitted, DateFormat.MDY)}
        />
        <CasDisplayField label="Status" value={data?.status} />
        <CasDisplayField label="Email" value={data?.email} />
        <CasDisplayField label="Upload" value={data?.type} />
      </CasStackRow>
      <CasStackRow className='bordered-cells'>
        <CasDisplayField label="Request" value={getParsedHTML(data?.requestProperty)} />
      </CasStackRow>
      <ClaimNotesField
        id={data.id}
        label="Processing Notes"
        url="Sce/Claims/RvwUpload/ProcessingNotes"
        value={data?.processingNotes}
        onUpdated={refetch}
      />
      <CasStackRow>
        {onViewClaimDetails && (
          <CasButton variant="text" onClick={onViewClaimDetails} sx={{ fontWeight: 'bold' }}>
            View Claim Details
          </CasButton>
        )}
        <CasButton
          variant="text"
          onClick={() => copyToSharepointMutate()}
          disabled={!data.sharepointFolderName || hasCopiedFiles}
        >
          Copy Files to Claim Folder
        </CasButton>
        <CasButton
          variant="text"
          onClick={() => openUploadsFolderInNewTab()}
          disabled={!data.sharepointFolderName}
        >
          Open Uploads Folder
        </CasButton>
        {data?.growerId && (
          <CasButton variant="text" onClick={growerDialog.setTrue}>
            Grower
          </CasButton>
        )}
      </CasStackRow>
      <CasTable
        columns={[
          {
            field: 'name',
            label: 'File Name',
            render: ({ row, column }) => (
              <CasLink onClick={() => downloadMutate({ id: row.id })}>
                {getCasTableCellValue({ row, column })}
              </CasLink>
            ),
          },
          { field: 'fileSize', label: 'Size' },
        ]}
        data={{ result: data?.files }}
      />
      {growerDialog.value && (
        <GrowerDialog
          id={data.growerId}
          insuredName={data.insuredName}
          onClose={growerDialog.setFalse}
          onUpdated={refetch}
        />
      )}
    </CasBaseDialog>
  );
}
